import React, { useState, useEffect } from 'react';
import { arabicToEnglish } from '../utils/numberConverter';
import {
  Box,
  Flex,
  Heading,
  Button,
  Card,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  Text,
  useDisclosure,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Spinner,
  Center,
  IconButton,
  useBreakpointValue,
} from '@chakra-ui/react';
import { useApp } from '../context/AppContext';
import { saudiRegions, regionNames } from '../data/saudiRegions';

const UserManagement = () => {
  const { users, locations, addUser, updateUser, deleteUser, canManageUsers, loadUsers, loadLocations, loadRegions, loadingUsers, usersPagination } = useApp();
  const [currentPage, setCurrentPage] = useState(1);
  const toast = useToast();
  
  // Load users, locations, and regions when component mounts
  useEffect(() => {
    loadUsers(currentPage);
    if (locations.length === 0) {
      loadLocations();
    }
    loadRegions(); // Load regions for region selection dropdown
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load users when page changes
  useEffect(() => {
    loadUsers(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const [editingUser, setEditingUser] = useState(null);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Responsive breakpoints for pagination
  const showPageNumbers = useBreakpointValue({ base: false, sm: true });
  const showPageInfo = useBreakpointValue({ base: false, md: true });
  const buttonSize = useBreakpointValue({ base: 'sm', md: 'md' });
  const pageButtonSize = useBreakpointValue({ base: 'xs', md: 'sm' });
  
  const [formData, setFormData] = useState({
    fullName: '',
    username: '',
    password: '',
    role: 'Examiner',
    region: '',
    city: '',
    locationId: '',
    rank: '',
    position: ''
  });

  if (!canManageUsers()) {
    return (
      <Card>
        <Box p={6}>
          <Box textAlign="center" color="gray.500">
            ليس لديك صلاحية للوصول إلى هذه الصفحة.
          </Box>
        </Box>
      </Card>
    );
  }

  // System Administrator cannot be created from frontend - only from backend for security
  const roles = [
    'Examiner',
    'Region Supervisor',
    'Location Supervisor',
    'Read-Only User'
  ];

  const roleLabels = {
    'System Administrator': 'مدير النظام',
    'Examiner': 'خبير',
    'Region Supervisor': 'مشرف منطقة',
    'Location Supervisor': 'مشرف موقع',
    'Read-Only User': 'مستخدم للقراءة فقط'
  };

  const handleOpenModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        fullName: user.fullName,
        username: user.username,
        password: '',
        role: user.role,
        region: user.region || '',
        city: user.city || '',
        locationId: user.locationId || user.location_id || '',
        rank: user.rank || '',
        position: user.position || ''
      });
    } else {
      setEditingUser(null);
      setFormData({
        fullName: '',
        username: '',
        password: '',
        role: 'Examiner',
        region: '',
        city: '',
        locationId: '',
        rank: '',
        position: ''
      });
    }
    onOpen();
  };

  const handleCloseModal = () => {
    onClose();
    setEditingUser(null);
    setFormData({
      fullName: '',
      username: '',
      password: '',
      role: 'Examiner',
      region: '',
      city: '',
      locationId: '',
      rank: '',
      position: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      // Prepare user data with proper null handling
        // Validate locationId is required
        if (!formData.locationId) {
          toast({
            title: 'خطأ',
            description: 'الموقع مطلوب',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
          return;
        }
        
        const userData = {
          fullName: formData.fullName,
          username: formData.username,
          password: formData.password,
          role: formData.role,
          region: formData.region || null,
          city: formData.city || null,
          locationId: formData.locationId, // Required - no null conversion
          rank: formData.rank || null,
          position: formData.position || null
        };
      
      if (editingUser) {
        // For update, only include password if provided
        const updateData = {
          fullName: userData.fullName,
          username: userData.username,
          role: userData.role,
          region: userData.region,
          city: userData.city,
          locationId: userData.locationId,
          rank: userData.rank,
          position: userData.position
        };
        if (userData.password) {
          updateData.password = userData.password;
        }
        await updateUser(editingUser.id, updateData);
        toast({
          title: 'تم التحديث',
          description: 'تم تحديث المستخدم بنجاح',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        // For create, password is required
        if (!userData.password) {
          toast({
            title: 'خطأ',
            description: 'كلمة المرور مطلوبة',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
          return;
        }
        await addUser(userData);
        toast({
          title: 'تمت الإضافة',
          description: 'تم إضافة المستخدم بنجاح',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
      handleCloseModal();
    } catch (error) {
      console.error('Error saving user:', error);
      // Extract error message from Laravel validation errors
      let errorMessage = 'فشل في حفظ المستخدم';
      if (error.response?.data) {
        if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.errors) {
          // Laravel validation errors format
          const errors = error.response.data.errors;
          const firstError = Object.values(errors)[0];
          errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast({
        title: 'خطأ',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteUserId(id);
    onDeleteOpen();
  };

  const handleDeleteConfirm = async () => {
    if (isDeleting) return;
    
    setIsDeleting(true);
    try {
      await deleteUser(deleteUserId);
      toast({
        title: 'تم الحذف',
        description: 'تم حذف المستخدم بنجاح',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onDeleteClose();
      setDeleteUserId(null);
    } catch (error) {
      console.error('Error deleting user:', error);
      toast({
        title: 'خطأ',
        description: error.response?.data?.message || error.message || 'فشل في حذف المستخدم',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg">إدارة المستخدمين</Heading>
        <Button colorScheme="primary" onClick={() => handleOpenModal()}>
          إضافة مستخدم جديد
        </Button>
      </Flex>

      <Card>
        {loadingUsers ? (
          <Box p={10}>
            <Center>
              <VStack spacing={4}>
                <Spinner size="xl" color="primary.500" thickness="4px" speed="0.65s" />
                <Text color="gray.600">جاري تحميل المستخدمين...</Text>
              </VStack>
            </Center>
          </Box>
        ) : (
        <Box overflowX="auto">
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>الاسم الكامل</Th>
                <Th>اسم المستخدم</Th>
                <Th>الدور</Th>
                <Th>المنطقة</Th>
                <Th>المدينة</Th>
                  <Th>الموقع</Th>
                <Th>الرتبة</Th>
                <Th>المنصب</Th>
                <Th>الإجراءات</Th>
              </Tr>
            </Thead>
            <Tbody>
              {users
                .filter(user => user.role !== 'System Administrator') // Hide System Administrator completely
                .map((user) => (
                <Tr key={user.id}>
                  <Td>{user.fullName}</Td>
                  <Td>{user.username}</Td>
                  <Td>
                    <Badge colorScheme="primary">
                      {roleLabels[user.role]}
                    </Badge>
                  </Td>
                  <Td>{user.region || '-'}</Td>
                  <Td>{user.city || '-'}</Td>
                  <Td>
                    {user.locationId || user.location_id 
                      ? (locations.find(loc => loc.id === (user.locationId || user.location_id))?.name || '-')
                      : '-'
                    }
                  </Td>
                  <Td>{user.rank || '-'}</Td>
                  <Td>{user.position || '-'}</Td>
                  <Td>
                    <HStack spacing={2}>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleOpenModal(user)}
                      >
                        تعديل
                      </Button>
                      <Button
                        size="sm"
                        colorScheme="red"
                        onClick={() => handleDeleteClick(user.id)}
                      >
                        حذف
                      </Button>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
        )}
      </Card>

      {/* Pagination */}
      {usersPagination && usersPagination.totalPages > 1 && (
        <Box mt={6}>
          <Flex 
            direction={{ base: 'column', md: 'row' }}
            justifyContent="center" 
            alignItems="center" 
            gap={{ base: 3, md: 4 }}
            flexWrap="wrap"
          >
            {/* Navigation Buttons */}
            <HStack spacing={2}>
              <IconButton
                aria-label="الصفحة السابقة"
                onClick={() => {
                  if (currentPage > 1) {
                    setCurrentPage(currentPage - 1);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
                isDisabled={currentPage === 1 || loadingUsers}
                variant="outline"
                size={buttonSize}
                fontSize="xl"
                colorScheme="gray"
              >
                <Text as="span" color="currentColor" fontWeight="bold">←</Text>
              </IconButton>
              
              {/* Page Numbers - Hidden on small screens, shown on medium+ */}
              {showPageNumbers && (
                <HStack spacing={1}>
                  {(() => {
                    const totalPages = usersPagination.totalPages || 1;
                    const current = currentPage;
                    const pages = [];
                    
                    // Show first page
                    if (current > 3) {
                      pages.push(1);
                      if (current > 4) {
                        pages.push('...');
                      }
                    }
                    
                    // Show pages around current
                    const start = Math.max(1, current - 2);
                    const end = Math.min(totalPages, current + 2);
                    
                    for (let i = start; i <= end; i++) {
                      pages.push(i);
                    }
                    
                    // Show last page
                    if (current < totalPages - 2) {
                      if (current < totalPages - 3) {
                        pages.push('...');
                      }
                      pages.push(totalPages);
                    }
                    
                    return pages.map((page, index) => {
                      if (page === '...') {
                        return (
                          <Text key={`ellipsis-${index}`} px={1} color="gray.500" fontSize="sm">
                            ...
                          </Text>
                        );
                      }
                      
                      return (
                        <Button
                          key={page}
                          size={pageButtonSize}
                          minW={{ base: '32px', md: '40px' }}
                          onClick={() => {
                            setCurrentPage(page);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          isDisabled={loadingUsers}
                          colorScheme={page === current ? 'primary' : 'gray'}
                          variant={page === current ? 'solid' : 'outline'}
                        >
                          {page}
                        </Button>
                      );
                    });
                  })()}
                </HStack>
              )}
              
              {/* Current page indicator for mobile */}
              <Text 
                display={{ base: 'block', sm: 'none' }} 
                fontSize="sm" 
                fontWeight="semibold"
                px={2}
              >
                {currentPage} / {usersPagination.totalPages || 1}
              </Text>
              
              <IconButton
                aria-label="الصفحة التالية"
                onClick={() => {
                  const totalPages = usersPagination.totalPages || 1;
                  if (currentPage < totalPages) {
                    setCurrentPage(currentPage + 1);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
                isDisabled={currentPage >= (usersPagination.totalPages || 1) || loadingUsers}
                variant="outline"
                size={buttonSize}
                fontSize="xl"
                colorScheme="gray"
              >
                <Text as="span" color="currentColor" fontWeight="bold">→</Text>
              </IconButton>
            </HStack>
            
            {/* Page Info - Hidden on small screens */}
            {showPageInfo && (
              <Text 
                fontSize="sm" 
                color="gray.600"
                textAlign="center"
              >
                صفحة {currentPage} من {usersPagination.totalPages || 1} ({usersPagination.totalItems || 0} مستخدم)
              </Text>
            )}
          </Flex>
        </Box>
      )}

      <Modal isOpen={isOpen} onClose={handleCloseModal} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingUser ? 'تعديل مستخدم' : 'إضافة مستخدم جديد'}
          </ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleSubmit}>
            <ModalBody>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>الاسم الكامل</FormLabel>
                  <Input
                    value={formData.fullName}
                    onChange={(e) => {
                      const convertedValue = arabicToEnglish(e.target.value);
                      setFormData({ ...formData, fullName: convertedValue });
                    }}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>اسم المستخدم</FormLabel>
                  <Input
                    value={formData.username}
                    onChange={(e) => {
                      const convertedValue = arabicToEnglish(e.target.value);
                      setFormData({ ...formData, username: convertedValue });
                    }}
                  />
                </FormControl>

                <FormControl isRequired={!editingUser}>
                  <FormLabel>
                    {editingUser ? 'كلمة المرور (اتركها فارغة للحفاظ على القديمة)' : 'كلمة المرور'}
                  </FormLabel>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontWeight="semibold" mb={2} color="gray.700">
                    الدور
                  </FormLabel>
                  <Select
                    value={formData.role}
                    onChange={(e) =>
                      setFormData({ ...formData, role: e.target.value })
                    }
                    isDisabled={editingUser && editingUser.role === 'System Administrator'}
                    size="lg"
                    focusBorderColor="primary.500"
                  >
                    {roles.map((role) => (
                      <option key={role} value={role}>
                        {roleLabels[role]}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontWeight="semibold" mb={2} color="gray.700">
                    المنطقة
                  </FormLabel>
                  <Select
                    value={formData.region}
                    onChange={(e) =>
                      setFormData({ ...formData, region: e.target.value, city: '' })
                    }
                    size="lg"
                    focusBorderColor="primary.500"
                  >
                    <option value="">اختر المنطقة</option>
                    {regionNames.map((region) => (
                      <option key={region} value={region}>
                        {region}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontWeight="semibold" mb={2} color="gray.700">
                    المدينة
                  </FormLabel>
                  <Select
                    value={formData.city}
                    onChange={(e) =>
                      setFormData({ ...formData, city: e.target.value })
                    }
                    size="lg"
                    focusBorderColor="primary.500"
                    isDisabled={!formData.region}
                  >
                    <option value="">اختر المدينة</option>
                    {formData.region && saudiRegions[formData.region]?.map((city) => (
                      <option key={city} value={city}>
                        {city}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontWeight="semibold" mb={2} color="gray.700">
                    الموقع
                  </FormLabel>
                  <Select
                    value={formData.locationId}
                    onChange={(e) =>
                      setFormData({ ...formData, locationId: e.target.value })
                    }
                    size="lg"
                    focusBorderColor="primary.500"
                    placeholder="اختر الموقع"
                    isRequired
                  >
                    <option value="">اختر الموقع</option>
                    {locations.length === 0 ? (
                      <option value="" disabled>لا توجد مواقع متاحة - يرجى إضافة مواقع أولاً من صفحة إدارة المواقع</option>
                    ) : (
                      locations
                        .filter(loc => !formData.region || loc.region === formData.region)
                        .map((location) => (
                          <option key={location.id} value={location.id}>
                            {location.name} ({location.region} - {location.city})
                          </option>
                        ))
                    )}
                  </Select>
                  <Text fontSize="xs" color="red.500" mt={1}>
                    الموقع إلزامي لجميع المستخدمين
                  </Text>
                </FormControl>

                <FormControl>
                  <FormLabel fontWeight="semibold" mb={2} color="gray.700">
                    الرتبة / المرتبة
                  </FormLabel>
                  <Input
                    value={formData.rank}
                    onChange={(e) => {
                      const convertedValue = arabicToEnglish(e.target.value);
                      setFormData({ ...formData, rank: convertedValue });
                    }}
                    size="lg"
                    focusBorderColor="primary.500"
                    placeholder="مثال: رائد، نقيب، ملازم أول..."
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontWeight="semibold" mb={2} color="gray.700">
                    المنصب (اختياري)
                  </FormLabel>
                  <Input
                    value={formData.position}
                    onChange={(e) => {
                      const convertedValue = arabicToEnglish(e.target.value);
                      setFormData({ ...formData, position: convertedValue });
                    }}
                    size="lg"
                    focusBorderColor="primary.500"
                    placeholder="مثال: رئيس القسم، مدير الإدارة..."
                  />
                </FormControl>
              </VStack>
            </ModalBody>
            <Box p={6} borderTop="1px solid" borderColor="gray.200">
              <HStack justify="flex-end" spacing={3}>
                <Button variant="outline" onClick={handleCloseModal}>
                  إلغاء
                </Button>
                <Button type="submit" colorScheme="primary" isLoading={isSubmitting} loadingText={editingUser ? 'جاري الحفظ...' : 'جاري الإضافة...'}>
                  {editingUser ? 'حفظ التعديلات' : 'إضافة'}
                </Button>
              </HStack>
            </Box>
          </form>
        </ModalContent>
      </Modal>

      <AlertDialog isOpen={isDeleteOpen} onClose={onDeleteClose} leastDestructiveRef={undefined}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              حذف المستخدم
            </AlertDialogHeader>
            <AlertDialogBody>
              هل أنت متأكد من حذف هذا المستخدم؟
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button onClick={onDeleteClose}>
                إلغاء
              </Button>
              <Button colorScheme="red" onClick={handleDeleteConfirm} mr={3} isLoading={isDeleting} loadingText="جاري الحذف...">
                حذف
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default UserManagement;
